import { PostPaymentRequest, TiktokInfluencerPost, YouTubeInfluencerPost } from '@round/api';
import { InstagramInfluencerPost } from 'Modules/Advertising/InfluencerPlan/types/Instagram.types';
import { Button, Popover, Skeleton } from '@round/ui-kit';
import styles from './PaymentRequestStatusCell.module.css';
import { ReactComponent as PaymentIcon } from 'assets/PaymentCard.svg';
import capitalize from 'lodash/capitalize';
import cn from 'classnames';
import { getPaymentTransactionStatusLabel } from 'Modules/Finance/Payments/helpers';
import { ReactComponent as InfoIcon } from 'assets/InfoCircle.svg';
import { useState } from 'react';

type Props = {
    isLoading?: boolean;
    influencerPost: TiktokInfluencerPost | InstagramInfluencerPost | YouTubeInfluencerPost;
    paymentRequest: PostPaymentRequest | null;
    onClickRequestPayment?: () => void;
};

const PaymentRequestStatusCell = ({ isLoading, influencerPost, paymentRequest, onClickRequestPayment }: Props) => {
    const [infoRef, setInfoRef] = useState<SVGSVGElement | null>(null);

    if (isLoading) {
        return <Skeleton />;
    }

    const manualPaymentStatus = influencerPost.payment_status;

    const isPaymentRequested = typeof influencerPost.payment_request_id === 'number';
    const isPaymentRequestAvailable = !isPaymentRequested && manualPaymentStatus !== 'PAID';

    const status = (() => {
        if (!isPaymentRequested) {
            return 'UNREQUESTED';
        }

        if (!paymentRequest?.payment_status) {
            return;
        }

        if (paymentRequest.payment_status !== 'UNPAID') {
            return paymentRequest.payment_status;
        }

        return 'REQUESTED';
    })();

    if ((isPaymentRequested && !paymentRequest) || !status) {
        // This case means something went wrong fetching the payment request. We don't have individual entity error states yet,
        // so we'll just show a dash.
        return <>-</>;
    }
    const displayStatus = status
        .split('_')
        .map((word) => capitalize(word.toLowerCase()))
        .join(' ');

    const detailStatusLabel = paymentRequest?.payment_transaction_status
        ? getPaymentTransactionStatusLabel({
              status: paymentRequest.payment_transaction_status,
              method: paymentRequest.payment_method,
              scheduledDate: paymentRequest.tipalti_scheduled_datetime,
          })
        : null;

    return (
        <div onClick={(e) => e.stopPropagation()} className={styles.container}>
            <span
                className={cn(styles.status, {
                    [styles.paid]: status === 'PAID',
                    [styles.inProgress]: status === 'IN_PROGRESS',
                    [styles.requested]: status === 'REQUESTED',
                    [styles.unrequested]: status === 'UNREQUESTED',
                    [styles.failed]: status === 'FAILED',
                })}
            >
                {displayStatus}
            </span>

            {status !== 'PAID' && detailStatusLabel && (
                <>
                    <InfoIcon ref={setInfoRef} className={styles.infoIcon} />
                    <Popover
                        anchorElement={infoRef}
                        showOn="hover"
                        options={{ modifiers: [{ name: 'offset', options: { offset: [0, 8] } }] }}
                    >
                        <span className={styles.detailStatus}>{detailStatusLabel}</span>
                    </Popover>
                </>
            )}

            {isPaymentRequestAvailable && onClickRequestPayment && (
                <Button
                    onClick={() => {
                        onClickRequestPayment();
                    }}
                    className={styles.button}
                >
                    <PaymentIcon className={styles.paymentIcon} />
                </Button>
            )}
        </div>
    );
};

export default PaymentRequestStatusCell;
